/**
 * @name useHomePage
 * @description Hook para gerenciar o dashboard principal do responsável
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import { useAlert } from '@/contexts/AlertContext';
import { useLoading } from '@/contexts/LoadingContext';
import { useUserContext } from '@/contexts/UserContext';
import { useMultiTenantServices } from '@/services/supabase/entities';
import { vwProdutosMaisConsumidosResponsavel } from '@/services/supabase/views';
import { IVwProdutosMaisConsumidosResponsavel } from '@/types/views/IVwProdutosMaisConsumidosResponsavel';
import { useCallback, useEffect, useRef, useState } from 'react';

interface IDashboardMetrics {
     saldo_total: number;
     gastos_mes_atual: number;
     gastos_mes_anterior: number;
     media_gastos_diarios: number;
     total_dependentes: number;
     dependentes_ativos: number;
}

interface IGastosPorDependente {
     dependente_id: number;
     nome: string;
     total_gastos: number;
     percentual: number;
}

interface IEvolucaoGastos {
     mes: string;
     total_gastos: number;
}

interface IProdutosFavoritos {
     produto_id: number;
     nome_produto: string;
     total_consumido: number;
     frequencia: number;
}

interface IMovimentacaoRecente {
     id: number | null;
     tipo_movimentacao: string | null;
     tipo_origem: string | null;
     valor: number | null;
     saldo_anterior: number | null;
     saldo_posterior: number | null;
     descricao: string | null;
     data_movimentacao: string | null;
}

export const useHomePage = () => {
     const { userData, userType } = useUserContext();
     const { showError } = useAlert();
     const { setPageLoading, loadingStates } = useLoading();
     const services = useMultiTenantServices();

     // Helper para verificar estado de loading da página
     const isPageLoading = loadingStates['page-loading'] || false;

     // Estado para controlar se o carregamento inicial já ocorreu para o usuário/contexto atual
     const [hasLoaded, setHasLoaded] = useState(false);
     const isLoadingRef = useRef(false);

     // Estados do dashboard
     const [metrics, setMetrics] = useState<IDashboardMetrics>({
          saldo_total: 0,
          gastos_mes_atual: 0,
          gastos_mes_anterior: 0,
          media_gastos_diarios: 0,
          total_dependentes: 0,
          dependentes_ativos: 0,
     });

     const [gastosPorDependente, setGastosPorDependente] = useState<IGastosPorDependente[]>([]);
     const [evolucaoGastos, setEvolucaoGastos] = useState<IEvolucaoGastos[]>([]);
     const [produtosFavoritos, setProdutosFavoritos] = useState<IProdutosFavoritos[]>([]);
     const [nomesDependentes, setNomesDependentes] = useState<string[]>([]);
     const [movimentacoesRecentes, setMovimentacoesRecentes] = useState<IMovimentacaoRecente[]>([]);

     // Resetar carregamento quando usuário/contexto mudar
     useEffect(() => {
          setHasLoaded(false);
          isLoadingRef.current = false;
     }, [userData?.id, userData?.fk_id_organizacao]);

     // Função para obter nome do tipo de usuário
     const getUserTypeLabel = useCallback((type: number | null) => {
          switch (type) {
               case 1:
                    return { label: 'Administrador', color: 'error' as const };
               case 2:
                    return { label: 'Coordenador', color: 'warning' as const };
               case 3:
                    return { label: 'Operador', color: 'info' as const };
               case 4:
                    return { label: 'Responsável', color: 'success' as const };
               default:
                    return { label: 'Usuário', color: 'default' as const };
          }
     }, []);

     // Carregar nomes dos dependentes
     const carregarNomesDependentes = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) return;

          try {
               // Buscar dependentes do responsável
               const dependentesResponse = await services.pessoasDependentes.getByResponsavel(userData.id!);

               if (dependentesResponse.data && dependentesResponse.data.length > 0) {
                    const nomesPromises = dependentesResponse.data.map(async (dependente: any) => {
                         try {
                              // Buscar nome do dependente
                              const pessoaResponse = await services.pessoas.getById(dependente.fk_id_aluno);
                              return pessoaResponse.data?.nome || `Dependente ${dependente.fk_id_aluno}`;
                         } catch (error) {
                              console.error('Erro ao buscar nome do dependente:', dependente.fk_id_aluno, error);
                              return `Dependente ${dependente.fk_id_aluno}`;
                         }
                    });

                    const nomes = await Promise.all(nomesPromises);
                    setNomesDependentes(nomes.filter(Boolean));
               } else {
                    setNomesDependentes([]);
               }
          } catch (error) {
               console.error('Erro ao carregar nomes dos dependentes:', error);
               setNomesDependentes([]);
          }
     }, [userData?.id, userData?.fk_id_organizacao, services]);

     // Carregar métricas do dashboard com dados reais
     const carregarMetricasDashboard = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) return;

          try {
               console.log('🔍 [useHomePage] Iniciando carregamento de dados...');

               // Buscar função do dashboard do Supabase
               console.log('🔍 [useHomePage] Chamando dashboard function com:', {
                    idResponsavel: userData.id,
                    idOrganizacao: userData.fk_id_organizacao,
               });

               const dashboardResponse = await services.dashboardFunctions.obterDashboardResponsavel(userData.id, userData.fk_id_organizacao);

               if (dashboardResponse.error) {
                    console.error('❌ [useHomePage] Erro ao carregar dashboard:', dashboardResponse.error);
                    // Usar dados padrão em caso de erro
                    setMetrics({
                         saldo_total: 0,
                         gastos_mes_atual: 0,
                         gastos_mes_anterior: 0,
                         media_gastos_diarios: 0,
                         total_dependentes: 0,
                         dependentes_ativos: 0,
                    });
                    // Limpar evolução de gastos quando há erro
                    setEvolucaoGastos([]);
                    return;
               }

               console.log('📊 [useHomePage] Resposta da dashboard function:', dashboardResponse.data);

               // Se a function não retornar dados válidos, tentar carregamento alternativo
               if (dashboardResponse.error || !dashboardResponse.data) {
                    try {
                         // Buscar vendas do responsável para calcular gastos do mês
                         const vendasResponse = await services.vendas.getVendasPorResponsavel(userData.id, 100);

                         if (vendasResponse.data && vendasResponse.data.length > 0) {
                              // Filtrar vendas do mês atual
                              const agora = new Date();
                              const mesAtual = agora.getMonth();
                              const anoAtual = agora.getFullYear();

                              const vendasMesAtual = vendasResponse.data.filter((venda: any) => {
                                   const dataVenda = new Date(venda.data_venda);
                                   return dataVenda.getMonth() === mesAtual && dataVenda.getFullYear() === anoAtual;
                              });

                              const gastosMesAtual = vendasMesAtual.reduce((sum: number, venda: any) => sum + (venda.valor_final || 0), 0);

                              // Definir métricas básicas
                              setMetrics({
                                   saldo_total: 0, // Precisaria buscar do saldo
                                   gastos_mes_atual: gastosMesAtual,
                                   gastos_mes_anterior: 0,
                                   media_gastos_diarios: gastosMesAtual / agora.getDate(),
                                   total_dependentes: 0,
                                   dependentes_ativos: 0,
                              });

                              // Criar evolução com dados reais
                              if (gastosMesAtual > 0) {
                                   const mesFormatado = `${anoAtual}-${String(mesAtual + 1).padStart(2, '0')}`;
                                   const evolucaoComDados = [{ mes: mesFormatado, total_gastos: gastosMesAtual }];

                                   console.log('📈 [useHomePage] Evolução criada:', evolucaoComDados);
                                   setEvolucaoGastos(evolucaoComDados);
                              } else {
                                   setEvolucaoGastos([]);
                              }
                         } else {
                              setMetrics({
                                   saldo_total: 0,
                                   gastos_mes_atual: 0,
                                   gastos_mes_anterior: 0,
                                   media_gastos_diarios: 0,
                                   total_dependentes: 0,
                                   dependentes_ativos: 0,
                              });
                              setEvolucaoGastos([]);
                         }
                    } catch (fallbackError) {
                         console.error('❌ [useHomePage] Erro no carregamento alternativo:', fallbackError);

                         setEvolucaoGastos([]);
                    }
                    return;
               }

               if (dashboardResponse.data) {
                    const result = typeof dashboardResponse.data === 'string' ? JSON.parse(dashboardResponse.data) : dashboardResponse.data;

                    if (result.success && result.data) {
                         setMetrics({
                              saldo_total: result.data.saldo_total || 0,
                              gastos_mes_atual: result.data.gastos_mes_atual || 0,
                              gastos_mes_anterior: result.data.gastos_mes_anterior || 0,
                              media_gastos_diarios: result.data.media_gastos_diarios || 0,
                              total_dependentes: result.data.total_dependentes || 0,
                              dependentes_ativos: result.data.dependentes_ativos || 0,
                         });

                         // Processar evolução de gastos se disponível
                         if (result.data.evolucao_gastos) {
                              setEvolucaoGastos(result.data.evolucao_gastos);
                         } else {

                              // Criar evolução básica com dados disponíveis
                              const agora = new Date();
                              const mesAtual = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;

                              // Calcular mês anterior corretamente
                              const dataAnterior = new Date(agora.getFullYear(), agora.getMonth() - 1);
                              const mesAnterior = `${dataAnterior.getFullYear()}-${String(dataAnterior.getMonth() + 1).padStart(2, '0')}`;

                              const evolucaoBasica = [
                                   { mes: mesAnterior, total_gastos: result.data.gastos_mes_anterior || 0 },
                                   { mes: mesAtual, total_gastos: result.data.gastos_mes_atual || 0 },
                              ];

                              setEvolucaoGastos(evolucaoBasica);
                         }
                    } else {
                         // Se não há dados válidos, definir evolução vazia
                         setEvolucaoGastos([]);
                    }
               } else {
                    // Se não há resposta de dados, definir evolução vazia
                    setEvolucaoGastos([]);
               }
          } catch (error) {
               console.error('Erro ao carregar métricas do dashboard:', error);
               showError('Erro ao carregar dados do dashboard');
          }
     }, [userData?.id, userData?.fk_id_organizacao, services, showError]);

     // Carregar produtos favoritos com dados reais
     const carregarProdutosFavoritos = useCallback(async () => {
          if (!userData?.fk_id_organizacao || !userData?.id) return;

          try {
               // Buscar produtos mais consumidos via view otimizada
               const now = new Date();
               const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`; // 'YYYY-MM-01'
               const response = await vwProdutosMaisConsumidosResponsavel.getByResponsavelMes(
                    userData.id,
                    userData.fk_id_organizacao,
                    mesAtual
               );
               if (response.data && response.data.length > 0) {
                    // Mapear para o formato esperado pelo estado
                    const produtos: IProdutosFavoritos[] = response.data.map((item: IVwProdutosMaisConsumidosResponsavel) => ({
                         produto_id: item.produto_id ?? 0,
                         nome_produto: item.nome_produto ?? '',
                         total_consumido: item.total_consumido ?? 0,
                         frequencia: item.frequencia ?? 0
                    })).slice(0, 3);
                    setProdutosFavoritos(produtos);
               } else {
                    setProdutosFavoritos([{ produto_id: 1, nome_produto: 'Nenhum produto vendido', total_consumido: 0, frequencia: 0 }]);
               }
          } catch (error) {
               console.error('Erro ao carregar produtos favoritos:', error);
               setProdutosFavoritos([]);
          }
     }, [userData?.fk_id_organizacao, userData?.id]);

     // Carregar gastos por dependente com dados reais
     const carregarGastosPorDependente = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) return;

          try {
               // Buscar dependentes do responsável
               const dependentesResponse = await services.pessoasDependentes.getByResponsavel(userData.id!);

               if (dependentesResponse.data && dependentesResponse.data.length > 0) {
                    const gastosPromises = dependentesResponse.data.map(async (dependente: any) => {
                         try {
                              // Buscar vendas do aluno (dependente)
                              const vendasResponse = await services.vendas.getVendasPorAluno(dependente.fk_id_aluno);

                              if (vendasResponse.data) {
                                   // Filtrar vendas do mês atual
                                   const agora = new Date();
                                   const mesAtual = agora.getMonth();
                                   const anoAtual = agora.getFullYear();

                                   const totalGastos = vendasResponse.data.reduce((total: number, venda: any) => {
                                        const dataVenda = new Date(venda.data_venda);
                                        if (dataVenda.getMonth() === mesAtual && dataVenda.getFullYear() === anoAtual) {
                                             return total + (venda.valor_final || 0);
                                        }
                                        return total;
                                   }, 0);

                                   // Buscar nome do dependente
                                   const pessoaResponse = await services.pessoas.getById(dependente.fk_id_aluno);

                                   return {
                                        dependente_id: dependente.fk_id_aluno,
                                        nome: pessoaResponse.data?.nome || `Dependente ${dependente.fk_id_aluno}`,
                                        total_gastos: totalGastos,
                                        percentual: 0, // Será calculado depois
                                   };
                              }
                         } catch (error) {
                              console.error('Erro ao processar dependente:', dependente.fk_id_aluno, error);
                         }

                         return null;
                    });

                    const gastosResults = await Promise.all(gastosPromises);
                    const gastosValidos = gastosResults.filter(Boolean) as IGastosPorDependente[];

                    // Calcular percentuais
                    const totalGeral = gastosValidos.reduce((total, gasto) => total + gasto.total_gastos, 0);

                    if (totalGeral > 0) {
                         gastosValidos.forEach((gasto) => {
                              gasto.percentual = (gasto.total_gastos / totalGeral) * 100;
                         });
                    }

                    setGastosPorDependente(gastosValidos);
               } else {
                    // Usar dados padrão se não há dependentes
                    setGastosPorDependente([{ dependente_id: 0, nome: 'Nenhum dependente encontrado', total_gastos: 0, percentual: 0 }]);
               }
          } catch (error) {
               console.error('Erro ao carregar gastos por dependente:', error);
               setGastosPorDependente([]);
          }
     }, [userData?.id, userData?.fk_id_organizacao, services]);

     const carregarMovimentacoesRecentes = useCallback(async () => {
          if (!userData?.id || !userData?.fk_id_organizacao) return;

          try {
               const response = await services.viewMovimentacoesRecentes.getMovimentacoesPorPessoa(userData.id);

               if (response.error) {
                    console.error('Erro ao carregar movimentações recentes:', response.error);
                    setMovimentacoesRecentes([]);
                    return;
               }

               if (!response.data) {
                    setMovimentacoesRecentes([]);
                    return;
               }

               const movimentacoesFlat = Array.isArray(response.data[0]) ? response.data.flat() : response.data;

               const movimentacoesMapeadas: IMovimentacaoRecente[] = movimentacoesFlat
                    .map((mov: any) => ({
                         id: mov.id ?? null,
                         tipo_movimentacao: mov.tipo_movimentacao ?? null,
                         tipo_origem: mov.tipo_origem ?? null,
                         valor: mov.valor ?? null,
                         saldo_anterior: mov.saldo_anterior ?? null,
                         saldo_posterior: mov.saldo_posterior ?? null,
                         descricao: mov.descricao ?? null,
                         data_movimentacao: mov.data_movimentacao ?? null,
                    }))
                    .sort((a: IMovimentacaoRecente, b: IMovimentacaoRecente) => {
                         if (!a.data_movimentacao || !b.data_movimentacao) return 0;
                         return new Date(b.data_movimentacao).getTime() - new Date(a.data_movimentacao).getTime();
                    })
                    .slice(0, 5);

               setMovimentacoesRecentes(movimentacoesMapeadas);
          } catch (error) {
               console.error('Erro ao carregar movimentações recentes:', error);
               setMovimentacoesRecentes([]);
          }
     }, [userData?.id, userData?.fk_id_organizacao, services]);

     // Carregar dados iniciais apenas uma vez por usuário/contexto
     useEffect(() => {
          const currentUserId = userData?.id;
          const currentOrgId = userData?.fk_id_organizacao;
          if (!currentUserId || !currentOrgId || hasLoaded || isLoadingRef.current || isPageLoading) {
               return;
          }
          isLoadingRef.current = true;
          setPageLoading(true);
          (async () => {
               try {
                    await carregarMetricasDashboard();
                    await Promise.all([
                         carregarNomesDependentes(),
                         carregarProdutosFavoritos(),
                         carregarGastosPorDependente(),
                         carregarMovimentacoesRecentes(),
                    ]);
                    setHasLoaded(true);
               } catch (error) {
                    console.error('Erro ao carregar dados do dashboard:', error);
                    showError('Erro ao carregar dados do dashboard');
               } finally {
                    setPageLoading(false);
                    isLoadingRef.current = false;
               }
          })();
     }, [
          userData?.id,
          userData?.fk_id_organizacao,
          hasLoaded,
          isPageLoading,
          carregarMetricasDashboard,
          carregarNomesDependentes,
          carregarProdutosFavoritos,
          carregarGastosPorDependente,
          carregarMovimentacoesRecentes,
          setPageLoading,
          showError,
     ]);

     // Metadados
     const metadados = {
          title: 'Dashboard do Responsável',
          description: 'Gerencie os créditos e acompanhe as atividades dos seus dependentes',
          userTypeInfo: getUserTypeLabel(userType),
          userName: userData?.nome || 'Usuário',
     };

     return {
          // Estados
          metrics,
          gastosPorDependente,
          evolucaoGastos,
          produtosFavoritos,
          nomesDependentes,
          movimentacoesRecentes,
          loading: isPageLoading, // ✅ Usando LoadingContext

          // Metadados
          metadados,

          // Funções
          carregarMetricasDashboard,
          carregarGastosPorDependente,
          carregarProdutosFavoritos,
          carregarMovimentacoesRecentes,
     };
};
